import React, { useState, useEffect } from "react";
import axiosInstance from "../../../../axios";
import { FaCheckCircle, FaTimesCircle, FaSearch, FaCalendarAlt, FaUserGraduate, FaInbox, FaFilter } from "react-icons/fa";
import { Loading } from "@/pages/Other/Loading";
import Modal from "@/pages/Other/Modal";

const StudentAttendanceList = () => {
    const [students, setStudents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [selectedStudent, setSelectedStudent] = useState(null);

    // Attendance state
    const [attendanceRecords, setAttendanceRecords] = useState([]);
    const [attendanceLoading, setAttendanceLoading] = useState(false);
    const [attendanceError, setAttendanceError] = useState(null);

    const [filters, setFilters] = useState({
        startDate: "",
        endDate: "",
    });

    // Fetch students
    const fetchStudents = async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await axiosInstance.get("/user/student");
            setStudents(Array.isArray(response.data?.data?.data) ? response?.data?.data?.data : []);
        } catch (err) {
            setError("Failed to fetch students.");
            setStudents([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch attendance
    const fetchAttendanceRecords = async (studentId, filterParams = {}) => {
        setAttendanceLoading(true);
        setAttendanceError(null);
        try {
            const response = await axiosInstance.get("/admin/get-student-attendance", {
                params: { ...filterParams, studentId },
            });
            setAttendanceRecords(response?.data?.data || []);
        } catch (err) {
            setAttendanceError("Failed to fetch attendance records.");
        } finally {
            setAttendanceLoading(false);
        }
    };

    useEffect(() => {
        fetchStudents();
    }, []);

    const getToday = () => new Date().toISOString().split("T")[0];

    // Open modal
    const handleViewAttendance = (student) => {
        const today = getToday();
        setSelectedStudent(student);
        setShowModal(true);
        setFilters({ startDate: today, endDate: today });
        fetchAttendanceRecords(student.userId || student.id, { startDate: today, endDate: today });
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleFilterSubmit = (e) => {
        e.preventDefault();
        if (selectedStudent) {
            fetchAttendanceRecords(selectedStudent.userId || selectedStudent.id, filters);
        }
    };

    const handleClearFilter = () => {
        const today = getToday();
        const defaultFilters = { startDate: today, endDate: today };
        setFilters(defaultFilters);
        if (selectedStudent) {
            fetchAttendanceRecords(selectedStudent.userId || selectedStudent.id, defaultFilters);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedStudent(null);
        setAttendanceRecords([]);
    };

    const getInitials = (firstName, lastName) => {
        const first = firstName ? firstName[0] : "";
        const last = lastName ? lastName[0] : "";
        return (first + last).toUpperCase() || "S";
    };

    const filteredStudents = students.filter((student) => {
        const nameStr = `${student.firstName || ""} ${student.lastName || ""}`.toLowerCase();
        const emailStr = (student.email || "").toLowerCase();
        const search = searchTerm.toLowerCase();
        return nameStr.includes(search) || emailStr.includes(search);
    });

    return (
        <div className="p-6 bg-[#f8fafc] min-h-screen">
            {/* Header */}
            <div className="mb-6 bg-gradient-to-r from-slate-800 to-slate-700 rounded-2xl p-6 shadow-md text-white">
                <h1 className="text-2xl font-bold flex items-center gap-2">
                    <FaUserGraduate /> Student Attendance Registry
                </h1>
                <p className="text-slate-300 text-sm mt-1">
                    Track, audit, and filter daily attendance logs for enrolled students.
                </p>
            </div>

            {/* Toolbar */}
            <div className="bg-white border border-slate-200 rounded-2xl p-4 mb-6 shadow-sm flex flex-col sm:flex-row gap-4 items-center justify-between">
                <div className="relative flex-1 max-w-md w-full">
                    <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <input
                        type="text"
                        placeholder="Search students by name or email..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-sm bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-350 transition-all"
                    />
                </div>
                <div className="text-xs text-slate-400 font-bold uppercase tracking-wider shrink-0">
                    Total: {filteredStudents.length} Students
                </div>
            </div>

            {/* Student Table */}
            {loading ? (
                <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 shadow-sm flex flex-col items-center justify-center">
                    <Loading />
                    <p className="text-slate-500 font-semibold mt-2">Loading students list...</p>
                </div>
            ) : error ? (
                <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 text-center text-rose-700 font-semibold shadow-sm">
                    {error}
                </div>
            ) : filteredStudents.length === 0 ? (
                <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center text-slate-400 shadow-sm flex flex-col items-center gap-2">
                    <FaInbox className="text-4xl text-slate-300" />
                    No students found matching your query.
                </div>
            ) : (
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="bg-slate-50 border-b border-slate-200 text-slate-400 text-xs font-bold uppercase tracking-wider">
                                    <th className="py-4 px-6 w-16">ID</th>
                                    <th className="py-4 px-6">Student Name</th>
                                    <th className="py-4 px-6">Email Address</th>
                                    <th className="py-4 px-6 text-right">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 text-slate-700 text-sm">
                                {filteredStudents.map((student) => {
                                    const fullName = student.lastName 
                                        ? `${student.firstName} ${student.lastName}` 
                                        : student.firstName;
                                    return (
                                        <tr key={student.id} className="hover:bg-slate-50/40 transition-all duration-150">
                                            <td className="py-4 px-6 text-slate-400 font-semibold">
                                                #{student.id}
                                            </td>
                                            <td className="py-4 px-6">
                                                <div className="flex items-center gap-3">
                                                    <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center font-bold text-xs border border-slate-200/50">
                                                        {getInitials(student.firstName, student.lastName)}
                                                    </div>
                                                    <span className="font-bold text-slate-800">{fullName}</span>
                                                </div>
                                            </td>
                                            <td className="py-4 px-6 text-slate-500">
                                                {student.email}
                                            </td>
                                            <td className="py-4 px-6 text-right">
                                                <button
                                                    className="bg-slate-850 bg-slate-800 hover:bg-slate-900 text-white rounded-xl py-1.5 px-4 text-xs font-bold uppercase tracking-wider transition-all shadow-sm active:scale-95 cursor-pointer"
                                                    onClick={() => handleViewAttendance(student)}
                                                >
                                                    View Attendance
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <Modal isOpen={showModal} onClose={handleCloseModal}>
                    <div className="p-6 space-y-5 max-w-3xl w-full">
                        <div className="border-b border-slate-150 pb-4">
                            <h3 className="text-xl font-bold text-slate-800">
                                Attendance Log: {selectedStudent?.firstName} {selectedStudent?.lastName || ""}
                            </h3>
                            <p className="text-xs text-slate-400 mt-1">Filter and view Check-In history for this student.</p>
                        </div>

                        {/* Date Filters Form */}
                        <form onSubmit={handleFilterSubmit} className="bg-slate-50 rounded-xl p-4 border border-slate-200/80 flex flex-wrap items-end gap-4">
                            <div className="flex-1 min-w-[150px]">
                                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-450 mb-1.5">
                                    Start Date
                                </label>
                                <input
                                    type="date"
                                    name="startDate"
                                    value={filters.startDate}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all"
                                />
                            </div>
                            <div className="flex-1 min-w-[150px]">
                                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-450 mb-1.5">
                                    End Date
                                </label>
                                <input
                                    type="date"
                                    name="endDate"
                                    value={filters.endDate}
                                    onChange={handleFilterChange}
                                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm bg-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-300 transition-all"
                                />
                            </div>
                            <div className="flex gap-2">
                                <button
                                    type="submit"
                                    className="bg-slate-800 hover:bg-slate-900 text-white rounded-lg py-2 px-4 text-xs font-semibold uppercase tracking-wider transition-all flex items-center gap-1.5 cursor-pointer"
                                >
                                    <FaFilter className="text-[10px]" /> Filter
                                </button>
                                <button
                                    type="button"
                                    onClick={handleClearFilter}
                                    className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-600 rounded-lg py-2 px-4 text-xs font-semibold uppercase tracking-wider transition-all cursor-pointer"
                                >
                                    Clear
                                </button>
                            </div>
                        </form>

                        {/* Attendance Table / Content */}
                        {attendanceLoading ? (
                            <div className="text-center py-10 flex flex-col items-center justify-center">
                                <Loading />
                                <p className="text-xs text-slate-400 font-semibold mt-1">Retrieving attendance logs...</p>
                            </div>
                        ) : attendanceError ? (
                            <p className="text-rose-600 text-center text-sm font-semibold py-4">{attendanceError}</p>
                        ) : (
                            <div className="border border-slate-200 rounded-xl overflow-hidden shadow-sm">
                                <div className="max-h-[300px] overflow-y-auto">
                                    <table className="w-full text-left border-collapse">
                                        <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 z-10 text-slate-400 text-xs font-bold uppercase tracking-wider">
                                            <tr>
                                                <th className="py-3.5 px-5">Date</th>
                                                <th className="py-3.5 px-4">Check-In Time</th>
                                                <th className="py-3.5 px-4">Verification</th>
                                                <th className="py-3.5 px-5 text-right">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 text-slate-700 text-sm">
                                            {attendanceRecords?.length > 0 ? (
                                                attendanceRecords.map((record, idx) => {
                                                    const isPresent = record.status?.toLowerCase() === "present";
                                                    return (
                                                        <tr key={idx} className="hover:bg-slate-50/30 transition-all">
                                                            <td className="py-3.5 px-5 font-semibold text-slate-800">
                                                                {record.date}
                                                            </td>
                                                            <td className="py-3.5 px-4 text-slate-500 font-medium">
                                                                {record.checkInTime || "--:--"}
                                                            </td>
                                                            <td className="py-3.5 px-4">
                                                                {record.verified === 1 ? (
                                                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600">
                                                                        <FaCheckCircle className="w-3.5 h-3.5" /> Verified
                                                                    </span>
                                                                ) : (
                                                                    <span className="inline-flex items-center gap-1 text-[11px] font-bold text-slate-400">
                                                                        <FaTimesCircle className="w-3.5 h-3.5" /> Pending
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="py-3.5 px-5 text-right">
                                                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider border ${
                                                                    isPresent 
                                                                        ? "bg-emerald-50 text-emerald-700 border-emerald-200" 
                                                                        : "bg-rose-50 text-rose-700 border-rose-200"
                                                                }`}>
                                                                    {record.status || "Absent"}
                                                                </span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })
                                            ) : (
                                                <tr>
                                                    <td colSpan={4} className="py-8 text-center text-slate-400 italic">
                                                        No records found for the selected date range.
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        {/* Modal Footer Actions */}
                        <div className="flex justify-end pt-3 border-t border-slate-100">
                            <button
                                onClick={handleCloseModal}
                                className="px-5 py-2 border border-slate-200 hover:bg-slate-50 text-slate-650 rounded-lg text-xs font-semibold uppercase tracking-wider transition-all cursor-pointer"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default StudentAttendanceList;
